import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { FileText, ShieldCheck, Loader2 } from 'lucide-react';

/**
 * Prompts authenticated users to accept the latest TOS + Privacy Policy.
 * Non-dismissable until the user checks the box and confirms — or logs out.
 */
export function LegalAcceptanceGate() {
  const { user, logout } = useAuth();
  const [needs, setNeeds] = useState(false);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState(null);

  useEffect(() => {
    if (!user) { setNeeds(false); return; }
    api.get('/me/legal-status')
      .then(r => {
        setNeeds(!!r.data?.needs_acceptance);
        setVersions(r.data?.current_versions);
      })
      .catch(() => setNeeds(false));
  }, [user]);

  const accept = async () => {
    if (!checked) return toast.error('Debe marcar la casilla para aceptar');
    setBusy(true);
    try {
      await api.post('/me/legal-accept');
      toast.success('Gracias por aceptar los términos');
      setNeeds(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al aceptar');
    } finally { setBusy(false); }
  };

  if (!user || !needs) return null;

  return (
    <Dialog open onOpenChange={() => { /* not dismissable */ }}>
      <DialogContent
        className="max-w-lg"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="legal-acceptance-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            Aceptación de términos legales
          </DialogTitle>
          <DialogDescription>
            Antes de continuar, debe revisar y aceptar los siguientes documentos.
            Puede consultarlos en cualquier momento desde su perfil.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 text-sm">
          <Link to="/legal/tos" target="_blank" rel="noopener" className="flex items-center gap-2 p-2.5 rounded-md border border-slate-200 hover:bg-slate-50" data-testid="legal-view-tos">
            <FileText className="h-4 w-4 text-slate-500" />
            <span className="flex-1">Términos de Servicio</span>
            {versions && <span className="text-xs text-slate-500">v{versions.tos_version}</span>}
          </Link>
          <Link to="/legal/privacidad" target="_blank" rel="noopener" className="flex items-center gap-2 p-2.5 rounded-md border border-slate-200 hover:bg-slate-50" data-testid="legal-view-privacy">
            <FileText className="h-4 w-4 text-slate-500" />
            <span className="flex-1">Política de Privacidad</span>
            {versions && <span className="text-xs text-slate-500">v{versions.privacy_version}</span>}
          </Link>
        </div>

        <label className="flex items-start gap-2.5 py-3 cursor-pointer">
          <Checkbox checked={checked} onCheckedChange={setChecked} data-testid="legal-acceptance-checkbox" />
          <span className="text-sm text-slate-700">
            He leído y acepto los <strong>Términos de Servicio</strong> y la <strong>Política de Privacidad</strong> vigentes.
          </span>
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={logout} data-testid="legal-acceptance-logout">Cerrar sesión</Button>
          <Button onClick={accept} disabled={!checked || busy} data-testid="legal-acceptance-accept">
            {busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Aceptar y continuar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
